import { z } from 'zod';

/**
 * Environment configuration schema
 * Works with both Vite (VITE_*) and Next.js (NEXT_PUBLIC_*) prefixes
 */

// Release stages for signup gating
export type ReleaseStage = 'pre-mvp' | 'mvp' | 'launch';

// Client-side environment variables
export const clientEnvSchema = z.object({
  // Supabase
  SUPABASE_URL: z.string().url(),
  SUPABASE_ANON_KEY: z.string().min(1),
  
  // App origin (e.g., https://lykbl.com or https://lykbl.ai)
  APP_ORIGIN: z.string().url().optional(),
  
  // Chat API
  CHAT_API_URL: z.string().url().optional(),
  
  // Feature flags
  ENABLE_DEV_MODE: z.string().optional().transform(v => v === 'true'),
  
  // Release stage for signup gating (pre-mvp | mvp | launch)
  RELEASE_STAGE: z.enum(['pre-mvp', 'mvp', 'launch']).optional().default('mvp'),
  
  // OAuth / Social login toggle
  ENABLE_SOCIAL_AUTH: z.string().optional().transform(v => v === 'true').default('false'),
});

// Server-side environment variables (Edge Functions, etc.)
export const serverEnvSchema = z.object({
  SUPABASE_SERVICE_ROLE_KEY: z.string().min(1),
  OPENAI_API_KEY: z.string().min(1),
  N8N_WEBHOOK_URL: z.string().url().optional(),
  ZAPIER_WEBHOOK_URL: z.string().url().optional(),
});

export type ClientEnv = z.infer<typeof clientEnvSchema>;
export type ServerEnv = z.infer<typeof serverEnvSchema>;

/**
 * Get client environment variables
 * Supports both Vite and Next.js environments
 */
export function getClientEnv(): ClientEnv {
  // Check for Vite-style env vars first, then Next.js
  const supabaseUrl = 
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SUPABASE_URL) ||
    process.env.NEXT_PUBLIC_SUPABASE_URL ||
    process.env.VITE_SUPABASE_URL ||
    '';
    
  const supabaseAnonKey = 
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SUPABASE_ANON_KEY) ||
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
    process.env.VITE_SUPABASE_ANON_KEY ||
    '';
    
  const appOrigin = 
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_APP_ORIGIN) ||
    process.env.NEXT_PUBLIC_APP_ORIGIN ||
    process.env.PUBLIC_APP_ORIGIN ||
    (typeof window !== 'undefined' ? window.location.origin : undefined);
    
  const chatApiUrl = 
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_CHAT_API_URL) ||
    process.env.NEXT_PUBLIC_CHAT_API_URL ||
    process.env.CHAT_API_URL ||
    undefined;
    
  const enableDevMode = 
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_ENABLE_DEV_MODE) ||
    process.env.NEXT_PUBLIC_ENABLE_DEV_MODE ||
    'false';
    
  const releaseStage = 
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_RELEASE_STAGE) ||
    process.env.NEXT_PUBLIC_RELEASE_STAGE ||
    'mvp';

  const enableSocialAuth = 
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_ENABLE_SOCIAL_AUTH) ||
    process.env.NEXT_PUBLIC_ENABLE_SOCIAL_AUTH ||
    'false';

  return clientEnvSchema.parse({
    SUPABASE_URL: supabaseUrl,
    SUPABASE_ANON_KEY: supabaseAnonKey,
    APP_ORIGIN: appOrigin,
    CHAT_API_URL: chatApiUrl,
    ENABLE_DEV_MODE: enableDevMode,
    RELEASE_STAGE: releaseStage,
    ENABLE_SOCIAL_AUTH: enableSocialAuth,
  });
}

/**
 * Get the current release stage
 */
export function getReleaseStage(): ReleaseStage {
  try {
    const env = getClientEnv();
    return env.RELEASE_STAGE;
  } catch {
    return 'mvp'; // Default to mvp
  }
}

/**
 * Check if social auth is enabled
 */
export function isSocialAuthEnabled(): boolean {
  try {
    const env = getClientEnv();
    return env.ENABLE_SOCIAL_AUTH;
  } catch {
    return false;
  }
}

/**
 * Check if public signup is allowed
 * Pre-MVP: No public signup at all
 * MVP: Only developers can signup (via OAuth), creators must go through Foundation
 * Launch: Full public signup available
 */
export function isPublicSignupAllowed(): boolean {
  const stage = getReleaseStage();
  return stage === 'launch';
}

/**
 * Check if developer OAuth signup is allowed
 */
export function isDeveloperSignupAllowed(): boolean {
  if (!isSocialAuthEnabled()) return false;
  const stage = getReleaseStage();
  return stage === 'mvp' || stage === 'launch';
}

/**
 * Check if creator OAuth signup is allowed
 */
export function isCreatorOAuthAllowed(): boolean {
  if (!isSocialAuthEnabled()) return false;
  const stage = getReleaseStage();
  return stage === 'mvp' || stage === 'launch';
}

/**
 * Determine which app we're running based on origin
 */
export type AppType = 'marketing' | 'ai-amp';

export function getAppType(origin?: string): AppType {
  const url = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  
  if (url.includes('lykbl.ai') || url.includes('app.lykbl.ai')) {
    return 'ai-amp';
  }
  
  return 'marketing';
}

/**
 * Get chat mode based on app type
 */
export type ChatMode = 'marketing' | 'ai-amp' | 'creator' | 'brand';

export function getChatMode(appType: AppType): ChatMode {
  return appType === 'ai-amp' ? 'ai-amp' : 'marketing';
}

